import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { format } from "date-fns";
import { useState } from "react";
import { Pressable } from "react-native";
import { useFragment } from "react-relay";

import {
  CalendarFilledIcon,
  ClockIcon,
  PlaceIcon,
} from "@whoosh/design-system/src/components/icons_v2/general";
import {
  Box,
  HStack,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";
import {
  convertDateStringToObj,
  formatHHMM,
} from "@whoosh/utilities/src/dates";

import { EventLocationsModal } from "./EventLocationsModal";
import { EventDateTimeLocationBar_eventSession$key } from "./__generated__/EventDateTimeLocationBar_eventSession.graphql";

type EventDateTimeLocationBarProps = {
  fragmentRef: EventDateTimeLocationBar_eventSession$key | null;
};

export const EventDateTimeLocationBar = ({
  fragmentRef,
}: EventDateTimeLocationBarProps) => {
  const [locationsModalOpen, setLocationsModalOpen] = useState(false);

  const data = useFragment(EventDateTimeLocationBarFragmentString, fragmentRef);

  const dateObj = convertDateStringToObj(data?.agenda.date);

  const formattedDate = dateObj ? format(dateObj, "EEE, MMM do, yyyy") : "";

  const formattedStartTime = data?.startAt ? formatHHMM(data.startAt) : "";

  const formattedEndTime = data?.endAt ? formatHHMM(data.endAt) : "";

  const timeString = data?.displayTime
    ? data.displayTime
    : `${formattedStartTime} - ${formattedEndTime}`;

  const courses =
    data?.courses?.edges?.flatMap((e) =>
      e?.node?.id
        ? [
            {
              id: e.node.id,
              name: e.node.name,
              bucketName: e.node.courseBucket?.name ?? null,
            },
          ]
        : []
    ) ?? [];

  const hasMultipleLocations = courses.length > 1;

  return (
    <>
      <Box
        backgroundColor="surface-secondary"
        borderRadius="2"
        padding={4}
        flexDirection="row"
        gap={4}
      >
        {/* Date column */}
        <VStack flex={1} gap={2}>
          <HStack gap={2} alignItems="center">
            <CalendarFilledIcon color="icon-primary" />
            <Text variant="mainTiny" color="text-primary">
              {t`Date`}
            </Text>
          </HStack>
          <Text variant="mainSmallSemiBold" color="text-primary">
            {formattedDate}
          </Text>
        </VStack>

        {/* Time column */}
        <VStack flex={1} gap={2}>
          <HStack gap={2} alignItems="center">
            <ClockIcon color="icon-primary" />
            <Text variant="mainTiny" color="text-primary">
              {t`Time`}
            </Text>
          </HStack>
          <Text variant="mainSmallSemiBold" color="text-primary">
            {timeString}
          </Text>
        </VStack>

        {/* Location column */}
        <VStack flex={1} gap={2}>
          <HStack gap={2} alignItems="center">
            <PlaceIcon color="icon-primary" />
            <Text variant="mainTiny" color="text-primary">
              {t`Location`}
            </Text>
          </HStack>
          {hasMultipleLocations ? (
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={t`View ${courses.length} locations`}
              onPress={() => setLocationsModalOpen(true)}
            >
              <Text
                variant="mainSmallSemiBold"
                color="text-primary"
                textDecorationLine="underline"
              >
                {`${courses.length} ${t`locations`}`}
              </Text>
            </Pressable>
          ) : (
            <Text variant="mainSmallSemiBold" color="text-primary">
              {courses[0]?.name ?? ""}
            </Text>
          )}
        </VStack>
      </Box>

      <EventLocationsModal
        isOpen={locationsModalOpen}
        onClose={() => setLocationsModalOpen(false)}
        locations={courses}
      />
    </>
  );
};

export const EventDateTimeLocationBarFragmentString = graphql`
  fragment EventDateTimeLocationBar_eventSession on EventSession {
    durationMins
    startAt
    endAt
    displayTime
    courses {
      edges {
        node {
          id
          name
          courseBucket {
            name
          }
        }
      }
    }
    agenda {
      date
    }
  }
`;
